import { put, call, getContext, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { trackPromise } from 'react-promise-tracker';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { placementModsAction } from './slice';
import { PlacementModsService } from 'app/services/PlacementModsServices/placementmods-service';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import {
  IPostEndDate,
  IPostTimeOff,
  IPostPayrollShiftDifferential,
  IUpdateShift,
  IPostReimbursement,
  badRequestError,
  effectiveDateErrorCode,
  IPostFurniture,
  iPostInsuranceMod,
  IPostTravelAllowanceMod,
  IPostPreferenceMod,
  IRefreshModDetailsRequest,
  IPostPTOMod,
  IUpdateOGP,
  IPostBillingShiftDifferential,
  MODIFICATIONS_GRID_PAGE_SIZE,
} from 'app/models/Placement/PlacementMods';
import i18next from 'i18next';
import {
  batchModSuccessResponseHandler,
  modsName,
  snackBarForModSave,
} from '@AMIEWEB/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/ModificationsUtils';
import { placementSummaryActions } from '../placement-summary/slice';
import { placementDetailsAction } from '../placement-details/slice';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { selectPlacementHeaderData } from '../placement-details/selectors';
import {
  getAndSetPlacementTimestamp,
} from '../placement-details/request.saga';
import { GridType } from 'app/components/Common/types';

const TrackSaveModDetailsAPI = (fn, ...args) => trackPromise(fn(...args), 'save-mod-api');
const TrackPostOperationsAPI = (fn, ...args) => trackPromise(fn(...args), 'post-mod-api');
const TrackDeleteModAPI = (fn, ...args) => trackPromise(fn(...args), 'delete-mod-api');

export function* addMinReqHoursModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addMinReqHoursModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.minReqHours);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (
      error?.response &&
      (error?.response?.data?.responseCode === effectiveDateErrorCode ||
        error?.response?.data?.responseCode === badRequestError)
    ) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Minimum Required Hours could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addMinReqHoursModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateMinReqHoursModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateMinReqHoursModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.minReqHours);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: ' Minimum Required Hours could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateMinReqHoursModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addPayRateModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addPayRateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.payRate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (
      error?.response &&
      (error?.response?.data?.responseCode === effectiveDateErrorCode ||
        error?.response?.data?.responseCode === badRequestError)
    ) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Pay Rate could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addPayRateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updatePayRateModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updatePayRateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.payRate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Pay Rate could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updatePayRateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addLodgingModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addLodgingModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.lodging);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Lodging Per Diem could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateMinReqHoursModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateLodgingModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateLodgingModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.lodging);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Lodging Per Diem could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateMinReqHoursModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateEndDateModDetails(action: PayloadAction<IPostEndDate>) {
  try {
    const placementModService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementModService.updateEndDateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.endDate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      const placementService: PlacementService = yield getContext('placementService');
      const currentPlacement = yield select(selectPlacementHeaderData);
      const headerResponse = yield call(placementService.getPlacementHeader, action.payload.placementId);
      if (httpSuccess(headerResponse?.status)) {
        yield put(
          placementDetailsAction.setPlacementHeaderDetails({
            ...currentPlacement,
            placementDate: {
              startDate: headerResponse.data.placementStartDate,
              endDate: headerResponse.data.placementEndDate,
            },
            placementStartDate: headerResponse.data.placementStartDate,
            placementEndDate: headerResponse.data.placementEndDate,
            lastUpdatedAt: headerResponse.data.lastUpdatedAt,
            lastUpdatedBy: headerResponse.data.lastUpdatedBy,
          }),
        );
        yield call(getAndSetPlacementTimestamp, {
          payload: { placementId: action.payload.placementId },
          type: 'get_set_placement_timestamp',
        });    
        return true;
      }
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: ' End Date Mod could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateEndDateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addEndDateModDetails(action: PayloadAction<IPostEndDate>) {
  try {
    const placementModService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementModService.addEndDateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.endDate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    const placementService: PlacementService = yield getContext('placementService');
    const currentPlacement = yield select(selectPlacementHeaderData);
    const headerResponse = yield call(placementService.getPlacementHeader, action.payload.placementId);
    if (httpSuccess(headerResponse?.status)) {
      yield put(
        placementDetailsAction.setPlacementHeaderDetails({
          ...currentPlacement,
          placementDate: {
            startDate: headerResponse.data.placementStartDate,
            endDate: headerResponse.data.placementEndDate,
          },
          placementStartDate: headerResponse.data.placementStartDate,
          placementEndDate: headerResponse.data.placementEndDate,
          lastUpdatedAt: headerResponse.data.lastUpdatedAt,
          lastUpdatedBy: headerResponse.data.lastUpdatedBy,
        }),
      );
      return true;
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: ' End Date Mod could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addEndDateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateShiftModDetails(action: PayloadAction<IUpdateShift>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateShiftModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.shift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    yield put(placementSummaryActions.getScheduleOrderRequirement({ placementId: action.payload.placementId }));
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Shift Mod could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateShiftModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addShiftModDetails(action: PayloadAction<IUpdateShift>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addShiftModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.shift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    yield put(placementSummaryActions.getScheduleOrderRequirement({ placementId: action.payload.placementId }));
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Shift could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addShiftModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateReimbursementModDetails(action: PayloadAction<IPostReimbursement>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateReimbursementModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.reimbursement);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Reimbursement Mod could not be saved. Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateReimbursementModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addReimbursementModDetails(action: PayloadAction<IPostReimbursement>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addReimbursementModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.reimbursement);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Reimbursement Mod could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addReimbursementModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* deleteMods(
  action: PayloadAction<{
    placementId: number;
    travelerOptionId: number;
    travelerOptionTimestamp: string;
    modTitle: string;
  }>,
) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackDeleteModAPI,
      placementService.deleteMods,
      action.payload.placementId,
      action.payload.travelerOptionId,
      action.payload.travelerOptionTimestamp,
    );
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.tabs.modificationsTab.deleteModification'),
          severity: 'success',
        }),
      );
      yield put(placementModsAction.setModSavedStatus(response.data));
      
      try {
        const placementId = action.payload.placementId;
        if (placementId) {
          yield put(placementModsAction.getModificationAction({
            pageNumber: 1,
            pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
            placementId: Number(placementId),
            offSet: 0,
            gridType: null,
            sortColumn: null,
            sortOrder: null,
          }));
        }
      } catch {}
      return true;
    } else {
      yield put(placementModsAction.setModSavedStatus(response.data));
      yield put(
        globalActions.setSnackBar({
          message: `${action.payload?.modTitle} could not be deleted.Please try again.`,
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    }
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'deleteMods',
        area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/delete-saga.ts',
      },
    });
  }
}

//Batch delete Mods

export function* batchDeleteMods(
  action: PayloadAction<{
    placementId: number;
    travelerOptionIds: string;
    travelerOptionTimestamps: string;
  }>,
) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackDeleteModAPI,
      placementService.batchDeleteMods,
      action.payload.placementId,
      action.payload.travelerOptionIds,
      action.payload.travelerOptionTimestamps,
    );

    const deletedMods = batchModSuccessResponseHandler(response?.data);
    const totalModsToDelete = response.data?.results?.length || 0;
    
    if (httpSuccess(response.status)) {
      if (deletedMods.length) {
        yield put(
          globalActions.setSnackBar({
            message: `${deletedMods.length}/${totalModsToDelete} ${i18next.t('placement.profile.tabs.modificationsTab.deleteModifications')}`,
            severity: 'success',
          }),
        );
      } else {
        yield put(
          globalActions.setSnackBar({
            message: `${i18next.t('placement.profile.tabs.modificationsTab.deleteModError', { placementId : action.payload.placementId })}`,
            severity: 'error',
          }),
       );
      }
      yield put(placementModsAction.setModSavedStatus(response?.data));

      const placementId = action.payload.placementId;
      if (placementId) {
        yield put(placementModsAction.getModificationAction({
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: Number(placementId),
          offSet: 0,
          gridType: GridType.modification,
          sortColumn: null,
          sortOrder: null,
        }));
      }
        
      return true;
      
    } else {
      yield put(
        globalActions.setSnackBar({
          message: `${i18next.t('placement.profile.tabs.modificationsTab.deleteModError', { placementId : action.payload.placementId })}`,
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    }
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'batchDeleteMods',
        area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/delete-saga.ts',
      },
    });
  }
}

export function* batchUpdateMods(
  action: PayloadAction<{
    placementId: number;
    modifications: []
  }>,
) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackDeleteModAPI,
      placementService.batchUpdateMods,
      { modifications: action.payload.modifications }
    );

    const updatedMods = batchModSuccessResponseHandler(response?.data);
    const totalModsToUpdate = response.data?.results?.length || 0;
    
    if (httpSuccess(response.status)) {
      if (updatedMods.length) {
        yield put(
          globalActions.setSnackBar({
            message: `${updatedMods.length}/${totalModsToUpdate} ${i18next.t('placement.profile.tabs.modificationsTab.updatedModifications')}`,
            severity: 'success',
          }),
        );
      } else {
        yield put(
          globalActions.setSnackBar({
            message: `${i18next.t('placement.profile.tabs.modificationsTab.updateModError', { placementId : action.payload.placementId })}`,
            severity: 'error',
          }),
        );        
      }

      yield put(placementModsAction.setModSavedStatus(response.data));
      return true;
      
    } else {
      yield put(
        globalActions.setSnackBar({
          message: `${i18next.t('placement.profile.tabs.modificationsTab.updateModError', { placementId : action.payload.placementId })}`,
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    }
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIResponseError,
        functionName: 'batchDeleteMods',
        area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/delete-saga.ts',
      },
    });
  }
}

//

export function* voidMods(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackPostOperationsAPI, placementService.voidMods, action.payload);
    if (httpSuccess(response.status) && response.data) {
      yield put(
        globalActions.setSnackBar({
          message: 'Voided successfully',
          severity: 'success',
        }),
      );
      yield put(placementModsAction.setModSavedStatus(response.data));
      return true;
    } else {
      yield put(placementModsAction.setModSavedStatus(response.data));
      yield put(
        globalActions.setSnackBar({
          message: `${action.payload?.modTitle} could not be voided.Please try again.`,
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: `${action.payload?.modTitle} could not be voided.Please try again.`,
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'voidMods',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/post-saga.ts',
        },
      });
    }
  }
}

export function* addStartDateModDetails(action: PayloadAction<any>) {
  const errorMessage = 'Start Date Mod could not be saved to save. Please try again';
  try {
    const placementModService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementModService.addStartDateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.startDate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    const placementService: PlacementService = yield getContext('placementService');
    const currentPlacement = yield select(selectPlacementHeaderData);
    const headerResponse = yield call(placementService.getPlacementHeader, action.payload.placementId);
    if (httpSuccess(headerResponse?.status)) {
      yield put(
        placementDetailsAction.setPlacementHeaderDetails({
          ...currentPlacement,
          placementDate: {
            startDate: headerResponse.data.placementStartDate,
            endDate: headerResponse.data.placementEndDate,
          },
          placementStartDate: headerResponse.data.placementStartDate,
          placementEndDate: headerResponse.data.placementEndDate,
          lastUpdatedAt: headerResponse.data.lastUpdatedAt,
          lastUpdatedBy: headerResponse.data.lastUpdatedBy,
        }),
      );
      return true;
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: errorMessage,
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addStartDateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateStartDateModDetails(action: PayloadAction<any>) {
  const errorMessage = 'Start Date Mod could not be saved to save. Please try again';
  try {
    const placementModService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementModService.updateStartDateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.startDate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    const placementService: PlacementService = yield getContext('placementService');
    const currentPlacement = yield select(selectPlacementHeaderData);
    const headerResponse = yield call(placementService.getPlacementHeader, action.payload.placementId);
    if (httpSuccess(headerResponse?.status)) {
      yield put(
        placementDetailsAction.setPlacementHeaderDetails({
          ...currentPlacement,
          placementDate: {
            startDate: headerResponse.data.placementStartDate,
            endDate: headerResponse.data.placementEndDate,
          },
          placementStartDate: headerResponse.data.placementStartDate,
          placementEndDate: headerResponse.data.placementEndDate,
          lastUpdatedAt: headerResponse.data.lastUpdatedAt,
          lastUpdatedBy: headerResponse.data.lastUpdatedBy,
        }),
      );
      if (httpSuccess(response?.status)) {
        yield call(getAndSetPlacementTimestamp, {
          payload: { placementId: action.payload.placementId },
          type: 'get_set_placement_timestamp',
        });    
      }
      return true;
    }
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: errorMessage,
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateStartDateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateEmiLmoModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateEmiLmoModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.emiLmo);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'EMI/LMO could not be saved. Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateEmiLmoModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* addEmiLmoModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addEmiLmoModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.emiLmo);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'EMI LMO could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addEmiLmoModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}
export function* addPreferenceModDetails(action: PayloadAction<IPostPreferenceMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addPreferenceModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.preference);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.placementMods.placementModsAPIMessages.preferenceModSaveFailure'),
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addPreferenceModDetails',
          area: '/src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* updateTimeOffModDetails(action: PayloadAction<IPostTimeOff>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateTimeOffModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.timeOff);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: ' Time Off Mod could not be saved. Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateTimeOffModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addTimeOffModDetails(action: PayloadAction<IPostTimeOff>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addTimeOffModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.timeOff);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: ' Time Off Mod could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addTimeOffModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateFurnitureModDetails(action: PayloadAction<IPostFurniture>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateFurnitureModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.furniture);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: ' Furniture Mod could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateFurnitureModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addFurnitureModDetails(action: PayloadAction<IPostFurniture>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addFurnitureModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.furniture);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: ' Furniture Mod could not be saved. Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addFurnitureModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateTravelAllowanceModDetails(action: PayloadAction<IPostTravelAllowanceMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.updateTravelAllowanceModDetails,
      action.payload,
    );
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, action.payload.selectedModType);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: `${action.payload.selectedModType} could not be saved. Please try again`,
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateEndingAndArrivingTravelModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}
export function* addArrivingTravelModDetails(action: PayloadAction<IPostTravelAllowanceMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addTravelAllowanceModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, action.payload.selectedModType);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (
      error?.response &&
      (error?.response?.data?.responseCode === effectiveDateErrorCode ||
        error?.response?.data?.responseCode === badRequestError)
    ) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: `${action.payload.selectedModType} could not be saved. Please try again`,
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addArrivingTravelModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* updatePayrollShiftDifferentialModDetails(action: PayloadAction<IPostPayrollShiftDifferential>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.updatePayrollShiftDifferentialModDetails,
      action.payload,
    );
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.payrollShift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Payroll Shift Differential Mod could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updatePayrollShiftDifferentialModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addPayrollShiftDifferentialModDetails(action: PayloadAction<IPostPayrollShiftDifferential>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.addPayrollShiftDifferentialModDetails,
      action.payload,
    );
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.payrollShift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Payroll Shift Differentia lMod could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addPayrollShiftDifferentialModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateInsuranceModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateInsuranceModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.insurance);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Insurance Mod could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateInsuranceModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* refreshModDetails(action: PayloadAction<IRefreshModDetailsRequest>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.refreshMod, action.payload);
    if (httpSuccess(response.status) && response.data) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('alerts.saveSuccess'),
          severity: 'success',
        }),
      );
      yield put(placementModsAction.setModSavedStatus(response.data));

      const placementId = action.payload.placementId;
      if (placementId) {
        // Pending mods grid
        yield put(placementModsAction.getModificationAction({
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: Number(placementId),
          offSet: 0,
          gridType: GridType.modification,
          sortColumn: null,
          sortOrder: null,
        }));

        // History mods grid
        yield put(placementModsAction.getModificationAction({
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: Number(placementId),
          offSet: 0,
          gridType: null,
          sortColumn: null,
          sortOrder: null,
        }));
      }
    } else {
      yield put(placementModsAction.setRefreshModDetails(response.data.message));
    }
    return true;
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(placementModsAction.setRefreshModDetails(error?.respose?.data?.message));
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'refreshModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addInsuranceModDetails(action: PayloadAction<iPostInsuranceMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addInsuranceModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.insurance);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: ' Insurance Mod could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addInsuranceModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addBillRateModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addBillRateModDetails, action.payload);

    yield put(placementModsAction.setModSavedStatus(response?.data));
    const snack = snackBarForModSave(response, modsName.billRate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Bill Rate Mod could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addBillRateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateBillRateModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateBillRateModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.billRate);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Bill Rate Mod could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateBillRateModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateBonusModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateBonusModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.bonus);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.bonusModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateBonusModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}
export function* updatePreferenceModDetails(action: PayloadAction<IPostPreferenceMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updatePreferenceModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.preference);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.preferenceModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updatePreferenceModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* addBonusModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addBonuseModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response?.data));
    const snack = snackBarForModSave(response, modsName.bonus);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.bonusModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addBonusModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* updateLinkModTask(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateLinkModTask, action.payload);
    if (httpSuccess(response.status) && response.data) {
      if (!response.data?.isModDeleted) {
        yield put(
          globalActions.setSnackBar({
            message: response.data?.updateMessage,
            severity: 'success',
          }),
        );
      }
      yield put(placementModsAction.setModSavedStatus(response.data));
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });
      
      const placementId = action.payload.placementId;
      if (placementId) {
        yield put(placementModsAction.getModificationAction({
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: Number(placementId),
          offSet: 0,
          gridType: GridType.modification,
          sortColumn: null,
          sortOrder: null,
        }));

        yield put(placementModsAction.getModificationAction({
          pageNumber: 1,
          pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
          placementId: Number(placementId),
          offSet: 0,
          gridType: null,
          sortColumn: null,
          sortOrder: null,
        }));
      }
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Unable to Update',
          severity: 'error',
        }),
      );
      yield put(placementModsAction.setModSavedStatus(response.data));
    }
  } catch (error) {
    if (error?.response) {
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Unable to Update',
          severity: 'error',
        }),
      );
      yield put(placementModsAction.setModSavedStatus(error.data));
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateLinkModTask',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateOGPModDetails(action: PayloadAction<IUpdateOGP>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateOGPModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.ogp);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.placementMods.placementModsAPIMessages.OGPModSaveFailure'),
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateOGPModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addOGPModDetails(action: PayloadAction<IUpdateOGP>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addOGPModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response?.data));
    const snack = snackBarForModSave(response, modsName.ogp);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.OGPModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addOGPModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}
export function* updatePTOModDetails(action: PayloadAction<IPostPTOMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updatePTOModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.pto);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.PTOModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updatePTOModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}
export function* addPTOModDetails(action: PayloadAction<IPostPTOMod>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addPTOModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.pto);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.PTOModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addPTOModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* updateBillingShiftDifferentialModDetails(action: PayloadAction<IPostBillingShiftDifferential>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.updateBillingShiftDifferentialModDetails,
      action.payload,
    );

    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.billingShift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: 'Billing Shift Differential Mod could not be saved to save.Please try again',
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateBillingShiftDifferentialModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* addBillingShiftDifferentialModDetails(action: PayloadAction<IPostBillingShiftDifferential>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.addBillingShiftDifferentialModDetails,
      action.payload,
    );
    yield put(placementModsAction.setModSavedStatus(response?.data));
    const snack = snackBarForModSave(response, modsName.billingShift);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: 'Billing Shift Differentia lMod could not be saved to save.Please try again',
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addBillingShiftDifferentialModDetails',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/Store/update-saga.ts',
        },
      });
    }
  }
}

export function* updateHousingModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.updateHousingModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response.data));
    const snack = snackBarForModSave(response, modsName.housing);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    if (httpSuccess(response?.status)) {
      yield call(getAndSetPlacementTimestamp, {
        payload: { placementId: action.payload.placementId },
        type: 'get_set_placement_timestamp',
      });    
    }
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.housingModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'updateHousingModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* addHousingModDetails(action: PayloadAction<any>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(TrackSaveModDetailsAPI, placementService.addHousingModDetails, action.payload);
    yield put(placementModsAction.setModSavedStatus(response?.data));
    const snack = snackBarForModSave(response, modsName.housing);
    yield put(
      globalActions.setSnackBar({
        message: snack.message,
        severity: snack.severity,
      }),
    );
    return httpSuccess(response?.status);
  } catch (error) {
    if (error?.response) {
      yield put(placementModsAction.setModSavedStatus(error?.response?.data));
    } else {
      if (!error?.response?.data || error?.response?.data?.responseCode < 0) {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('placement.placementMods.placementModsAPIMessages.housingModSaveFailure'),
            severity: 'error',
          }),
        );
      }
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.APIRequestError,
          functionName: 'addHousingModDetails',
          area: 'src/store/redux-store/placement-mods/update-saga.ts',
        },
      });
    }
  }
}

export function* saveRefreshContractPayroll(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementModsService = yield getContext('placementModsService');
    const response = yield call(
      TrackSaveModDetailsAPI,
      placementService.saveRefreshContractPayroll,
      action.payload.placementId,
    );
    
    // Store response in Redux state - component will handle success/error snackbars
    yield put(placementModsAction.setRefreshContractPayrollSaveResponse({
      success: response?.data?.responseCode === 0,
      responseCode: response?.data?.responseCode,
      message: response?.data?.message,
      data: response?.data
    }));
    
    // Optionally refresh the grid after successful save
    if (response?.data?.responseCode === 0) {
      yield put(placementModsAction.getModificationAction({
        pageNumber: 1,
        pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
        placementId: action.payload.placementId,
        offSet: 0,
        gridType: GridType.modification,
        sortColumn: null,
        sortOrder: null,
      }));
    }
  } catch (error) {
    yield put(placementModsAction.setRefreshContractPayrollSaveResponse({
      success: false,
      responseCode: error?.response?.data?.responseCode || 61423,
      message: error?.response?.data?.message || 'An error occurred while updating the placement',
      data: error?.response?.data
    }));
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'saveRefreshContractPayroll',
        area: 'src/store/redux-store/placement-mods/update-saga.ts',
      },
    });
  }
}